import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getComments,
  createComment,
  deleteComment,
  likeComment,
  unlikeComment,
  reportComment,
} from '../services/feedApi';
import { getAuthToken, getAuthUserSnapshot } from '../utils/auth';
import { socket as authenticatedSocket } from '../services/socket';

function toSafeArray(value) {
  return Array.isArray(value) ? value : [];
}



function normalizeSign(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function mergeUniqueComments(currentItems, incomingItems) {
  const map = new Map();

  [...toSafeArray(currentItems), ...toSafeArray(incomingItems)].forEach((item) => {
    if (!item?.id) return;

    const key = String(item.id);
    const previous = map.get(key);

    if (!previous) {
      map.set(key, item);
      return;
    }

    if (previous?._optimistic && !item?._optimistic) {
      map.set(key, item);
      return;
    }

    if (!previous?._optimistic && item?._optimistic) {
      return;
    }

    const previousTime = new Date(previous?.updatedAt || previous?.createdAt || 0).getTime();
    const nextTime = new Date(item?.updatedAt || item?.createdAt || 0).getTime();

    map.set(key, nextTime >= previousTime ? item : previous);
  });

  return Array.from(map.values()).sort((a, b) => {
    const aTime = new Date(a?.createdAt || 0).getTime();
    const bTime = new Date(b?.createdAt || 0).getTime();
    return aTime - bTime;
  });
}

function createOptimisticComment({ text = '' } = {}) {
  const nowIso = new Date().toISOString();
  const tempId = `temp-feed-comment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const viewer = getAuthUserSnapshot() || {};

  return {
    id: tempId,
    text: String(text || '').trim(),
    status: 'active',
    createdAt: nowIso,
    updatedAt: nowIso,
    author: {
      id: String(viewer?.id || viewer?._id || ''),
      fullName: viewer?.fullName || viewer?.name || 'Vos',
      username: viewer?.username || '',
      zodiacSign: viewer?.zodiacSign || viewer?.sign || viewer?.sunSign || '',
      avatarUrl:
        viewer?.avatarUrl ||
        viewer?.photoPath ||
        viewer?.profilePicture ||
        viewer?.profilePhoto ||
        viewer?.profileImage ||
        viewer?.avatar ||
        viewer?.image ||
        viewer?.photo ||
        viewer?.photoURL ||
        '',
    },
    stats: {
      likeCount: 0,
    },
    viewer: {
      isOwner: true,
    },
    _optimistic: true,
    _clientRequestKey: tempId,
  };
}


function removeCommentFromList(items, targetId) {
  return toSafeArray(items).filter((item) => String(item?.id || '') !== String(targetId || ''));
}

function extractOptimisticComments(items) {
  return toSafeArray(items).filter((item) => item?._optimistic);
}

function mergeServerCommentsPreservingOptimistic(currentItems, incomingItems) {
  const optimistic = extractOptimisticComments(currentItems);
  return mergeUniqueComments(optimistic, Array.isArray(incomingItems) ? incomingItems : []);
}

function reconcileCreatedComment(items, optimisticComment, createdComment) {
  const safeItems = toSafeArray(items);
  const optimisticId = String(optimisticComment?.id || '');
  const createdId = String(createdComment?.id || '');

  if (!createdId) {
    return removeCommentFromList(safeItems, optimisticId);
  }

  const withoutOptimistic = removeCommentFromList(safeItems, optimisticId);
  return mergeUniqueComments(withoutOptimistic, [createdComment]);
}

export default function useFeedComments(options = {}) {
  const {
    postId,
    initialLimit = 20,
    autoLoad = false,
    onCommentCreated,
    onCommentDeleted,
  } = options;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const mountedRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const pendingDeleteIdsRef = useRef(new Set());
  const pendingLikeCommentIdsRef = useRef(new Set());
  const socketRef = useRef(null);
  const itemsRef = useRef([]);
  const creatingRef = useRef(false);
  const lastLoadedPostIdRef = useRef('');
  const socketGenerationRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initialLoadDoneRef.current = false;
    setItems([]);
    setError('');
    pendingDeleteIdsRef.current = new Set();
    pendingLikeCommentIdsRef.current = new Set();
    itemsRef.current = [];
    lastLoadedPostIdRef.current = '';
  }, [postId]);

 
  const viewerSnapshot = useMemo(() => getAuthUserSnapshot(), []);
  const viewerSign = useMemo(
    () => normalizeSign(
      viewerSnapshot?.zodiacSign ||
      viewerSnapshot?.sign ||
      viewerSnapshot?.sunSign ||
      ''
    ),
    [viewerSnapshot]
  );
  const safePostId = useMemo(() => String(postId || ''), [postId]);
  const count = useMemo(() => items.length, [items]);
  const hasComments = useMemo(() => count > 0, [count]);

  useEffect(() => {
    itemsRef.current = Array.isArray(items) ? items : [];
    creatingRef.current = creating;
  }, [items, creating]);

  const loadComments = useCallback(async () => {
    if (!safePostId) {
      setItems([]);
      return [];
    }

    const token = getAuthToken();

    setLoading(true);
    setError('');

    try {
      const result = await getComments({
        token,
        postId: safePostId,
        limit: initialLimit,
      });

      const nextItems = Array.isArray(result?.items) ? result.items : [];
      lastLoadedPostIdRef.current = safePostId;

      if (!mountedRef.current) return nextItems;

      setItems((prev) =>
        mergeServerCommentsPreservingOptimistic(prev, nextItems)
      );
      return nextItems;
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.message || 'No se pudieron cargar los comentarios.');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [initialLimit, safePostId]);

  const refreshComments = useCallback(async () => {
    return loadComments();
  }, [loadComments]);

  const handleRealtimeCommentCreated = useCallback((payload = {}) => {
    const payloadPostId = String(payload?.postId || '');
    if (!safePostId || payloadPostId !== safePostId) return;

    const comment = payload?.comment || null;
    const commentId = String(comment?.id || '');
    if (!comment || !commentId) return;

    const currentItems = toSafeArray(itemsRef.current);
    const alreadyExists = currentItems.some(
      (item) => String(item?.id || '') === commentId
    );

    if (alreadyExists) return;

    const matchingOptimistic = currentItems.find((item) => {
      if (!item?._optimistic) return false;

      const sameAuthor = String(item?.author?.id || '') !== ''
        && String(item?.author?.id || '') === String(comment?.author?.id || '');
      const sameText = String(item?.text || '').trim() === String(comment?.text || '').trim();

      return sameAuthor && sameText;
    });

    setItems((prev) => {
      if (matchingOptimistic?.id) {
        return reconcileCreatedComment(prev, matchingOptimistic, comment);
      }

      return mergeUniqueComments(prev, [comment]);
    });
  }, [safePostId]);

  const handleRealtimeCommentDeleted = useCallback((payload = {}) => {
    const payloadPostId = String(payload?.postId || '');
    const commentId = String(payload?.commentId || '');
    if (!safePostId || payloadPostId !== safePostId || !commentId) return;

    setItems((prev) => removeCommentFromList(prev, commentId));
  }, [safePostId]);

  const handleRealtimeCommentReactionUpdated = useCallback((payload = {}) => {
    const payloadPostId = String(payload?.postId || '');
    const commentId = String(payload?.commentId || '');
    if (!safePostId || payloadPostId !== safePostId || !commentId) return;

    const nextLikeCount = Number(payload?.likeCount || 0);

    setItems((prev) =>
      prev.map((item) => {
        if (String(item?.id || '') !== commentId) return item;
        return {
          ...item,
          stats: {
            ...(item?.stats || {}),
            likeCount: nextLikeCount,
          },
        };
      })
    );
  }, [safePostId]);

  const submitComment = useCallback(
    async ({ text }) => {
      if (!safePostId) {
        const err = new Error('Post inválido.');
        setError(err.message);
        throw err;
      }

      if (creatingRef.current) return null;

      const token = getAuthToken();

      setCreating(true);
      setError('');

      const optimisticComment = createOptimisticComment({ text });

      if (mountedRef.current) {
        setItems((prev) => mergeUniqueComments(prev, [optimisticComment]));
      }

      try {
        const result = await createComment({
          token,
          postId: safePostId,
          text,
        });

        const createdComment = result?.comment || null;

        if (!mountedRef.current) return createdComment;

        setItems((prev) => reconcileCreatedComment(prev, optimisticComment, createdComment));

        if (typeof onCommentCreated === 'function' && createdComment) {
          onCommentCreated(createdComment);
        }

        return createdComment;
                 } catch (err) {
        if (mountedRef.current) {
          setItems((prev) => removeCommentFromList(prev, optimisticComment.id));
          setError(err?.message || 'No se pudo crear el comentario.');
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setCreating(false);
        }
      }
    },
    [onCommentCreated, safePostId]
  );

  const removeComment = useCallback(
    async (commentId) => {
      const safeCommentId = String(commentId || '');
      if (!safeCommentId) return null;
      if (pendingDeleteIdsRef.current.has(safeCommentId)) return null;

      const token = getAuthToken();

      setError('');
      pendingDeleteIdsRef.current.add(safeCommentId);

      const currentItems = toSafeArray(itemsRef.current);
      const existingComment = currentItems.find(
        (item) => String(item?.id || '') === safeCommentId
      );

      try {
        if (existingComment) {
          setItems((prev) => removeCommentFromList(prev, safeCommentId));
        }

        const result = await deleteComment({
          token,
          commentId: safeCommentId,
        });

        if (!mountedRef.current) return result;

        setItems((prev) =>
          prev.filter((item) => String(item?.id || '') !== safeCommentId)
        );

        if (typeof onCommentDeleted === 'function') {
          onCommentDeleted(result);
        }

        return result;
      } catch (err) {
  if (!mountedRef.current) return null;

  const currentItems = toSafeArray(itemsRef.current);
  const alreadyRestored = currentItems.some(
    (item) => String(item?.id || '') === safeCommentId
  );

  if (!alreadyRestored && existingComment) {
    setItems((prev) => mergeUniqueComments(prev, [existingComment]));
  }

  setError(err?.message || 'No se pudo eliminar el comentario.');
  throw err;
} finally {
        pendingDeleteIdsRef.current.delete(safeCommentId);
      }
    },
    [onCommentDeleted]
  );

  const toggleLikeComment = useCallback(
    async (comment) => {
      const safeCommentId = String(comment?.id || '');
      if (!safeCommentId) return null;
      if (pendingLikeCommentIdsRef.current.has(safeCommentId)) return null;

      const token = getAuthToken();
      const currentItems = toSafeArray(itemsRef.current);
      const existingComment = currentItems.find(
        (item) => String(item?.id || '') === safeCommentId
      ) || comment;

      const previousLiked = !!existingComment?.viewer?.liked;
      const previousLikeCount = Number(existingComment?.stats?.likeCount || 0);
      const optimisticLiked = !previousLiked;
      const optimisticLikeCount = optimisticLiked
        ? previousLikeCount + 1
        : Math.max(0, previousLikeCount - 1);
      const action = previousLiked ? unlikeComment : likeComment;

      pendingLikeCommentIdsRef.current.add(safeCommentId);

      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((item) => {
            if (String(item?.id || '') !== safeCommentId) return item;
            return {
              ...item,
              stats: {
                ...(item?.stats || {}),
                likeCount: optimisticLikeCount,
              },
              viewer: {
                ...(item?.viewer || {}),
                liked: optimisticLiked,
              },
            };
          })
        );
      }

      try {
        const result = await action({
          token,
          commentId: safeCommentId,
        });

        if (mountedRef.current) {
          setItems((prev) =>
            prev.map((item) => {
              if (String(item?.id || '') !== safeCommentId) return item;
              return {
                ...item,
                stats: {
                  ...(item?.stats || {}),
                  likeCount: Number(result?.likeCount ?? optimisticLikeCount),
                },
                viewer: {
                  ...(item?.viewer || {}),
                  liked: typeof result?.liked === 'boolean' ? !!result.liked : optimisticLiked,
                },
              };
            })
          );
        }

        return result;
      } catch (err) {
        if (mountedRef.current) {
          setItems((prev) =>
            prev.map((item) => {
              if (String(item?.id || '') !== safeCommentId) return item;
              return {
                ...item,
                stats: {
                  ...(item?.stats || {}),
                  likeCount: previousLikeCount,
                },
                viewer: {
                  ...(item?.viewer || {}),
                  liked: previousLiked,
                },
              };
            })
          );
          setError(err?.message || 'No se pudo actualizar el me gusta.');
        }
        throw err;
      } finally {
        pendingLikeCommentIdsRef.current.delete(safeCommentId);
      }
    },
    []
  );

  const submitCommentReport = useCallback(
    async (commentOrId, options = {}) => {
      const safeCommentId = String(
        commentOrId?.commentId ||
        commentOrId?.id ||
        commentOrId ||
        ''
      ).trim();

      if (!safeCommentId) return null;

      const token = getAuthToken();

      return reportComment({
        token,
        commentId: safeCommentId,
        reason: String(options?.reason || 'other').trim() || 'other',
        details: String(options?.details || '').trim(),
      });
    },
    []
  );

  useEffect(() => {
    const token = getAuthToken();
    if (!token || !safePostId) {
      socketRef.current = null;
      return undefined;
    }

    socketGenerationRef.current += 1;
    const generation = socketGenerationRef.current;
    let disposed = false;

    const socket = authenticatedSocket;

    socketRef.current = socket;

    const subscribe = () => {
      if (disposed) return;
      if (socketGenerationRef.current !== generation) return;

      socket.emit('feed:subscribe', {
        mode: 'para-vos',
        zodiacSign: viewerSign,
        postId: safePostId,
      });
    };

    socket.on('connect', subscribe);
    socket.on('feed:comment_created', handleRealtimeCommentCreated);
    socket.on('feed:comment_deleted', handleRealtimeCommentDeleted);
    socket.on('feed:comment_reaction_updated', handleRealtimeCommentReactionUpdated);

    if (socket.connected) subscribe();

    return () => {
      disposed = true;

      const isCurrentSocket = socketGenerationRef.current === generation;

      socket.off('connect', subscribe);
      socket.off('feed:comment_created', handleRealtimeCommentCreated);
      socket.off('feed:comment_deleted', handleRealtimeCommentDeleted);
      socket.off('feed:comment_reaction_updated', handleRealtimeCommentReactionUpdated);

      try {
        if (isCurrentSocket && socket.connected) {
          socket.emit('feed:unsubscribe', {
            mode: 'para-vos',
            zodiacSign: viewerSign,
            postId: safePostId,
          });
        }
      } catch {}

      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [
    safePostId,
    viewerSign,
    handleRealtimeCommentCreated,
    handleRealtimeCommentDeleted,
    handleRealtimeCommentReactionUpdated,
  ]);

  useEffect(() => {
    if (!autoLoad || !safePostId || initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    loadComments();
  }, [autoLoad, loadComments, safePostId]);

  return {
    items,
    count,
    hasComments,
    loading,
    creating,
    error,
    loadComments,
    refreshComments,
    submitComment,
    removeComment,
    toggleLikeComment,
    submitCommentReport,
    setItems,
    setError,
  };
}
